import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Box, MenuItem, Stack, Typography } from '@mui/material';
import { isMobile } from 'react-device-detect';
import { useTournamentHistory, useTournamentLeaderboard } from '../../History/HistoryQueries';
import type {
  TournamentHistoryEntry,
  TournamentHistoryFilters,
} from '../../History/Type/HistoryTypes';
import {
  COLORS,
  MONO_FONT,
  MTT_PAGE_SIZE,
  MTT_FILTER_DEFAULT,
  scrollContainerSx,
  tableHeaderSx,
  tableRowSx,
  toggleSortColumn,
  type MttFilterDraft,
  type MttFilter,
  type TournamentResultSelection,
  type TriStateSelection,
  type SortState,
  type MttSortColumn,
} from './HistoryConstants';
import {
  formatDateTime,
  formatAmount,
  parseDate,
  formatMonthLabel,
  formatTournamentStatus,
  formatGameTypeShort,
  getGameTypeColor,
} from './HistoryFormatters';
import { buildTournamentFilters, getMttSortParams } from './HistoryFilters';
import {
  StatusChip,
  SortableHeader,
  EmptyState,
  LoadingState,
  LoadingMore,
  ErrorState,
  DetailHeader,
  GroupHeader,
  FilterChip,
  FilterTextField,
  FilterGrid,
  RangeField,
  FilterActions,
  FilterPanel,
  CopyIdButton,
  DetailIdDisplay,
  TableHeaderLabel,
  sectionBoxSx,
  sectionTitleSx,
} from './HistoryUI';

interface TournamentHistoryTabProps {
  userId: string;
  active: boolean;
}

const TournamentHistoryTab: React.FC<TournamentHistoryTabProps> = ({ userId, active }) => {
  const [filterDraft, setFilterDraft] = useState<MttFilterDraft>({ ...MTT_FILTER_DEFAULT });
  const [filters, setFilters] = useState<TournamentHistoryFilters>(() => buildTournamentFilters(MTT_FILTER_DEFAULT));
  const [selectedTournament, setSelectedTournament] = useState<TournamentHistoryEntry | null>(null);
  const [filterOpen, setFilterOpen] = useState(false);
  const [sort, setSort] = useState<SortState<MttSortColumn>>({ column: 'date', direction: 'desc' });
  const scrollRef = useRef<HTMLDivElement | null>(null);

  const toggleSort = (col: MttSortColumn) => setSort((prev) => toggleSortColumn(prev, col));

  // Reset on deactivate
  useEffect(() => {
    if (!active) {
      setSelectedTournament(null);
    }
  }, [active]);

  // Scroll to top on filter change
  useEffect(() => {
    if (!active || selectedTournament) return;
    scrollRef.current?.scrollTo({ top: 0 });
  }, [filters, selectedTournament, active]);

  const updateFilter = useCallback((patch: Partial<MttFilterDraft>) => {
    setFilterDraft((prev) => ({ ...prev, ...patch }));
  }, []);

  const applyQuickFilter = useCallback(
    (status: MttFilter) => {
      const next = { ...filterDraft, status };
      setFilterDraft(next);
      setFilters(buildTournamentFilters(next));
    },
    [filterDraft]
  );

  const handleApplyFilters = useCallback(() => {
    setSelectedTournament(null);
    setFilters(buildTournamentFilters(filterDraft));
  }, [filterDraft]);

  const handleResetFilters = useCallback(() => {
    setSelectedTournament(null);
    setFilterDraft({ ...MTT_FILTER_DEFAULT });
    setFilters(buildTournamentFilters(MTT_FILTER_DEFAULT));
  }, []);

  // ─── Queries ────────────────────────────────────────────────────────────────

  const mttSortParams = getMttSortParams(sort);

  const tournamentQuery = useTournamentHistory({
    userId,
    filters,
    pageSize: MTT_PAGE_SIZE,
    sortBy: mttSortParams.sortBy,
    sortDirection: mttSortParams.sortDirection,
    enabled: active,
  });

  const leaderboardQuery = useTournamentLeaderboard({
    tournamentGameId: selectedTournament?.tournamentGameId,
    enabled: active && Boolean(selectedTournament?.tournamentGameId),
  });

  // ─── Memos ──────────────────────────────────────────────────────────────────

  const tournamentItems = useMemo(() => {
    return tournamentQuery.data?.pages.flatMap((page) => page.items ?? []) ?? [];
  }, [tournamentQuery.data]);

  const tournamentGroups = useMemo(() => {
    if (sort.column !== 'date') return [];

    const groups: Array<{ key: string; label: string; items: TournamentHistoryEntry[] }> = [];
    let currentKey: string | null = null;
    let currentGroup: (typeof groups)[number] | null = null;

    tournamentItems.forEach((item) => {
      const date = parseDate(item.startTime);
      const key = date ? `${date.getFullYear()}-${date.getMonth() + 1}` : 'unknown';
      if (key !== currentKey) {
        currentKey = key;
        currentGroup = {
          key,
          label: key === 'unknown' ? 'Unknown month' : formatMonthLabel(date),
          items: [],
        };
        groups.push(currentGroup);
      }
      currentGroup?.items.push(item);
    });
    return groups;
  }, [tournamentItems, sort.column]);

  // ─── Handlers ───────────────────────────────────────────────────────────────

  const handleScroll = useCallback(
    (event: React.UIEvent<HTMLDivElement>) => {
      const { scrollTop, scrollHeight, clientHeight } = event.currentTarget;
      if (scrollHeight - scrollTop - clientHeight < 200) {
        if (!selectedTournament && tournamentQuery.hasNextPage && !tournamentQuery.isFetchingNextPage) {
          tournamentQuery.fetchNextPage();
        }
      }
    },
    [selectedTournament, tournamentQuery]
  );

  const openTournamentDetail = (item: TournamentHistoryEntry) => {
    if (!item.tournamentGameId) return;
    setSelectedTournament(item);
    scrollRef.current?.scrollTo({ top: 0 });
  };

  const closeTournamentDetail = () => setSelectedTournament(null);

  // ─── Table grid templates ────────────────────────────────────────────────────

  const tournamentGridColumns = isMobile
    ? '34px 1fr 75px 70px'
    : '34px 1fr 90px 55px 70px 110px 80px';

  // ─── Render: tournament table header ────────────────────────────────────────

  const renderTournamentTableHeader = () => (
    <Box sx={{ ...tableHeaderSx, gridTemplateColumns: tournamentGridColumns }}>
      <TableHeaderLabel label="Id" align="center" />
      <SortableHeader label="Name" active={sort.column === 'name'} direction={sort.direction} onClick={() => toggleSort('name')} />
      <SortableHeader label="Status" active={sort.column === 'status'} direction={sort.direction} onClick={() => toggleSort('status')} />
      {!isMobile && <SortableHeader label="Pos" active={sort.column === 'pos'} direction={sort.direction} onClick={() => toggleSort('pos')} />}
      {!isMobile && <SortableHeader label="Buy-in" active={sort.column === 'buyin'} direction={sort.direction} onClick={() => toggleSort('buyin')} />}
      <SortableHeader label="Date" active={sort.column === 'date'} direction={sort.direction} onClick={() => toggleSort('date')} />
      <SortableHeader label="Prize" active={sort.column === 'prize'} direction={sort.direction} onClick={() => toggleSort('prize')} align="right" />
    </Box>
  );

  // ─── Render: tournament row ─────────────────────────────────────────────────

  const renderTournamentRow = (entry: TournamentHistoryEntry, index: number) => {
    const prize = entry.user?.prize;
    const position = entry.user?.position;
    const totalPlayers = entry.totalPlayers;
    const hasPrize = typeof prize === 'number' && prize > 0;
    const isCancelled = entry.status?.toUpperCase() === 'CANCELLED' || entry.status?.toUpperCase() === 'CANCELED';
    const isCompleted = entry.status?.toUpperCase() === 'COMPLETED';
    const showPosition = !isCancelled && typeof position === 'number';

    return (
      <Box
        key={entry.tournamentGameId}
        onClick={isCompleted ? () => openTournamentDetail(entry) : undefined}
        sx={{ ...tableRowSx(index, isCompleted), gridTemplateColumns: tournamentGridColumns }}
      >
        <CopyIdButton id={entry.tournamentGameId} label="tournament" />
        <Typography sx={{ fontSize: '14px', fontWeight: 500, color: COLORS.textPrimary, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {entry.name || 'Tournament'}
        </Typography>
        <StatusChip status={entry.status} />
        {!isMobile && (
          <Typography sx={{ fontSize: '13px', fontWeight: 600, color: COLORS.textPrimary, fontFamily: MONO_FONT }}>
            {showPosition ? `#${position}${totalPlayers ? `/${totalPlayers}` : ''}` : '\u2014'}
          </Typography>
        )}
        {!isMobile && (
          <Typography sx={{ fontSize: '13px', color: COLORS.textMuted }}>
            {formatAmount(entry.buyIn)}
          </Typography>
        )}
        <Typography sx={{ fontSize: '13px', color: COLORS.textMuted, whiteSpace: 'nowrap' }}>
          {formatDateTime(entry.startTime)}
        </Typography>
        {hasPrize ? (
          <Typography sx={{ fontSize: '14px', fontWeight: 700, color: COLORS.win, textAlign: 'right', fontFamily: MONO_FONT, letterSpacing: '-0.3px' }}>
            +{formatAmount(prize)}
          </Typography>
        ) : (
          <Typography sx={{ fontSize: '13px', color: COLORS.textMuted, textAlign: 'right' }}>{'\u2014'}</Typography>
        )}
      </Box>
    );
  };

  // ─── Render: tournament detail view (ranking only) ─────────────────────────

  const renderTournamentDetail = () => {
    const t = selectedTournament!;
    const isCancelled = t.status?.toUpperCase() === 'CANCELLED' || t.status?.toUpperCase() === 'CANCELED';
    const position = t.user?.position;
    const prize = t.user?.prize;
    const hasPrize = typeof prize === 'number' && prize > 0;
    const showPosition = !isCancelled && typeof position === 'number';
    const statusInfo = formatTournamentStatus(t.status);
    const gameTypeColor = getGameTypeColor(t.gameType);

    const resultGridColumns = isMobile ? '60px 1fr 80px' : '80px 1fr 100px';

    return (
      <Box>
        <DetailHeader
          title={t.name || 'Tournament'}
          subtitle={formatDateTime(t.startTime)}
          onBack={closeTournamentDetail}
          rightContent={
            <Stack direction="row" spacing={1} alignItems="center">
              {t.gameType && (
                <Box component="span" sx={{ fontSize: '12px', fontWeight: 700, fontFamily: 'Inter, sans-serif', color: gameTypeColor.color, bgcolor: gameTypeColor.bg, borderRadius: '4px', px: 1, py: 0.25, lineHeight: 1.6 }}>
                  {formatGameTypeShort(t.gameType)}
                </Box>
              )}
              <Box component="span" sx={{ fontSize: '12px', fontWeight: 700, color: statusInfo.color, bgcolor: statusInfo.bg, borderRadius: '4px', px: 1, py: 0.25, lineHeight: 1.6 }}>
                {statusInfo.label}
              </Box>
            </Stack>
          }
        />

        <Stack spacing={0.75} sx={{ mt: 0.75 }}>
          {/* Tournament summary */}
          <Box sx={sectionBoxSx}>
            <Box sx={{ mb: 0.75 }}>
              <DetailIdDisplay label="Tournament ID" id={t.tournamentGameId} copyLabel="tournament" />
            </Box>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: isMobile ? 1 : 2, rowGap: 0.75 }}>
              <SummaryItem label="Buy-in" value={formatAmount(t.buyIn)} />
              {typeof t.entryFee === 'number' && t.entryFee > 0 && (
                <SummaryItem label="Entry Fee" value={formatAmount(t.entryFee)} />
              )}
              {typeof t.prizePool === 'number' && (
                <SummaryItem label="Prize Pool" value={formatAmount(t.prizePool)} />
              )}
              {typeof t.totalPlayers === 'number' && (
                <SummaryItem label="Players" value={`${t.totalPlayers}`} />
              )}
              {typeof t.totalHandCount === 'number' && (
                <SummaryItem label="Hands" value={`${t.totalHandCount}`} />
              )}
            </Box>
          </Box>

          {/* Your Result */}
          <Box sx={{ ...sectionBoxSx, borderColor: hasPrize ? COLORS.winBorder : undefined }}>
            <Typography sx={sectionTitleSx}>Your Result</Typography>

            {/* Column headers */}
            <Box sx={{ display: 'grid', gridTemplateColumns: resultGridColumns, gap: 1, px: 1, pb: 0.5, borderBottom: `1px solid ${COLORS.borderLight}` }}>
              <Typography sx={{ fontSize: '11px', fontWeight: 700, color: COLORS.textMuted, textTransform: 'uppercase', letterSpacing: '0.4px' }}>Rank</Typography>
              <Typography sx={{ fontSize: '11px', fontWeight: 700, color: COLORS.textMuted, textTransform: 'uppercase', letterSpacing: '0.4px' }}>Player</Typography>
              <Typography sx={{ fontSize: '11px', fontWeight: 700, color: COLORS.textMuted, textTransform: 'uppercase', letterSpacing: '0.4px', textAlign: 'right' }}>Prize</Typography>
            </Box>

            {/* Result row */}
            <Box sx={{
              display: 'grid',
              gridTemplateColumns: resultGridColumns,
              gap: 1,
              alignItems: 'center',
              px: 1,
              py: 0.75,
              borderRadius: '4px',
              bgcolor: hasPrize ? COLORS.winBg : 'transparent',
            }}>
              <Typography sx={{ fontSize: '14px', fontWeight: 700, color: COLORS.textPrimary, fontFamily: MONO_FONT }}>
                {showPosition ? `#${position}` : '\u2014'}
              </Typography>
              <Box>
                <Typography sx={{ fontSize: '13px', fontWeight: 600, color: COLORS.textPrimary }}>
                  {t.user?.username || '\u2014'}
                </Typography>
                {showPosition && typeof t.totalPlayers === 'number' && (
                  <Typography sx={{ fontSize: '12px', color: COLORS.textMuted }}>
                    out of {t.totalPlayers} players
                  </Typography>
                )}
              </Box>
              <Typography sx={{
                fontSize: '14px',
                fontWeight: 700,
                fontFamily: MONO_FONT,
                textAlign: 'right',
                color: hasPrize ? COLORS.win : COLORS.textDisabled,
              }}>
                {isCancelled ? '\u2014' : hasPrize ? `+${formatAmount(prize)}` : '\u2014'}
              </Typography>
            </Box>
          </Box>

          {/* Elimination info */}
          {t.user?.eliminatedAt && !isCancelled && (
            <Box sx={sectionBoxSx}>
              <Typography sx={{ fontSize: '13px', color: COLORS.textMuted }}>
                Eliminated {formatDateTime(t.user.eliminatedAt)}
              </Typography>
            </Box>
          )}

          {/* Rebuy / Addon info */}
          {(typeof t.user?.rebuyCount === 'number' && t.user.rebuyCount > 0 || typeof t.user?.addonCount === 'number' && t.user.addonCount > 0) && (
            <Box sx={sectionBoxSx}>
              <Box sx={{ display: 'flex', gap: 2 }}>
                {typeof t.user?.rebuyCount === 'number' && t.user.rebuyCount > 0 && (
                  <SummaryItem label="Rebuys" value={`${t.user.rebuyCount}`} />
                )}
                {typeof t.user?.addonCount === 'number' && t.user.addonCount > 0 && (
                  <SummaryItem label="Add-ons" value={`${t.user.addonCount}`} />
                )}
              </Box>
            </Box>
          )}

          {/* Full Leaderboard */}
          <Box sx={sectionBoxSx}>
            <Typography sx={sectionTitleSx}>Leaderboard</Typography>
            {leaderboardQuery.isLoading && (
              <Typography sx={{ fontSize: '13px', color: COLORS.textMuted, py: 1, textAlign: 'center' }}>Loading leaderboard...</Typography>
            )}
            {leaderboardQuery.error && (
              <Typography sx={{ fontSize: '13px', color: COLORS.textMuted, py: 1, textAlign: 'center' }}>Leaderboard not available.</Typography>
            )}
            {!leaderboardQuery.isLoading && !leaderboardQuery.error && (!leaderboardQuery.data || leaderboardQuery.data.length === 0) && (
              <Typography sx={{ fontSize: '13px', color: COLORS.textMuted, py: 1, textAlign: 'center' }}>Leaderboard not available yet.</Typography>
            )}
            {!leaderboardQuery.isLoading && !leaderboardQuery.error && leaderboardQuery.data && leaderboardQuery.data.length > 0 && (
              <Box>
                {/* Column headers */}
                <Box sx={{ display: 'grid', gridTemplateColumns: resultGridColumns, gap: 1, px: 1, pb: 0.5, borderBottom: `1px solid ${COLORS.borderLight}` }}>
                  <Typography sx={{ fontSize: '11px', fontWeight: 700, color: COLORS.textMuted, textTransform: 'uppercase', letterSpacing: '0.4px' }}>Rank</Typography>
                  <Typography sx={{ fontSize: '11px', fontWeight: 700, color: COLORS.textMuted, textTransform: 'uppercase', letterSpacing: '0.4px' }}>Player</Typography>
                  <Typography sx={{ fontSize: '11px', fontWeight: 700, color: COLORS.textMuted, textTransform: 'uppercase', letterSpacing: '0.4px', textAlign: 'right' }}>Prize</Typography>
                </Box>
                {/* Rows */}
                {leaderboardQuery.data.map((entry, index) => {
                  const isCurrentUser = entry.userId === userId;
                  const entryHasPrize = typeof entry.prize === 'number' && entry.prize > 0;
                  return (
                    <Box
                      key={`${entry.userId}-${index}`}
                      sx={{
                        display: 'grid',
                        gridTemplateColumns: resultGridColumns,
                        gap: 1,
                        alignItems: 'center',
                        px: 1,
                        py: 0.5,
                        borderRadius: '3px',
                        bgcolor: isCurrentUser
                          ? 'rgba(39, 141, 148, 0.1)'
                          : index % 2 === 0
                            ? 'transparent'
                            : 'rgba(255, 255, 255, 0.015)',
                        borderLeft: isCurrentUser ? `2px solid ${COLORS.teal}` : '2px solid transparent',
                        '&:hover': { bgcolor: isCurrentUser ? 'rgba(39, 141, 148, 0.15)' : COLORS.rowHoverBg },
                      }}
                    >
                      <Typography sx={{ fontSize: '13px', fontWeight: 700, color: COLORS.textPrimary, fontFamily: MONO_FONT }}>
                        {typeof entry.position === 'number' ? `#${entry.position}` : '\u2014'}
                      </Typography>
                      <Typography sx={{
                        fontSize: '13px',
                        fontWeight: isCurrentUser ? 700 : 500,
                        color: isCurrentUser ? COLORS.teal : COLORS.textPrimary,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}>
                        {entry.username || entry.userId || `Player ${index + 1}`}
                        {isCurrentUser ? ' (you)' : ''}
                      </Typography>
                      <Typography sx={{
                        fontSize: '13px',
                        fontWeight: entryHasPrize ? 700 : 400,
                        fontFamily: MONO_FONT,
                        textAlign: 'right',
                        color: entryHasPrize ? COLORS.win : COLORS.textMuted,
                      }}>
                        {entryHasPrize ? `+${formatAmount(entry.prize)}` : '\u2014'}
                      </Typography>
                    </Box>
                  );
                })}
              </Box>
            )}
          </Box>
        </Stack>
      </Box>
    );
  };

  // ─── Main render ────────────────────────────────────────────────────────────

  return (
    <>
      {!selectedTournament && (
        <>
          <Stack direction="row" spacing={0.75} sx={{ mb: 1.5, flexWrap: 'wrap', gap: 0.5 }}>
            <FilterChip active={filterDraft.status === 'all'} onClick={() => applyQuickFilter('all')} color="#d1d1d2">
              All
            </FilterChip>
            <FilterChip active={filterDraft.status === 'completed'} onClick={() => applyQuickFilter('completed')} color="#d1d1d2">
              Completed
            </FilterChip>
            <FilterChip active={filterDraft.status === 'running'} onClick={() => applyQuickFilter('running')} color="#d1d1d2">
              Running
            </FilterChip>
            <FilterChip active={filterDraft.status === 'cancelled'} onClick={() => applyQuickFilter('cancelled')} color="#d1d1d2">
              Cancelled
            </FilterChip>
          </Stack>
          <FilterPanel
            title="Filters"
            actions={<FilterActions onApply={handleApplyFilters} onReset={handleResetFilters} />}
            open={filterOpen}
            onToggle={() => setFilterOpen(!filterOpen)}
          >
            <FilterGrid>
              <FilterTextField
                label="Tournament Name"
                placeholder="Search by name"
                value={filterDraft.nameContains}
                onChange={(e) => updateFilter({ nameContains: e.target.value })}
              />
              <FilterTextField
                select
                label="Result"
                value={filterDraft.result}
                onChange={(e) => updateFilter({ result: e.target.value as TournamentResultSelection })}
              >
                <MenuItem value="ALL">All results</MenuItem>
                <MenuItem value="CASHED">Cashed</MenuItem>
                <MenuItem value="UNCASHED">Uncashed</MenuItem>
                <MenuItem value="CANCELLED">Cancelled</MenuItem>
              </FilterTextField>
              <FilterTextField
                select
                label="Satellite"
                value={filterDraft.isSatellite}
                onChange={(e) => updateFilter({ isSatellite: e.target.value as TriStateSelection })}
              >
                <MenuItem value="ANY">Any</MenuItem>
                <MenuItem value="TRUE">Yes</MenuItem>
                <MenuItem value="FALSE">No</MenuItem>
              </FilterTextField>
              <FilterTextField
                select
                label="Rebuy Enabled"
                value={filterDraft.rebuyEnabled}
                onChange={(e) => updateFilter({ rebuyEnabled: e.target.value as TriStateSelection })}
              >
                <MenuItem value="ANY">Any</MenuItem>
                <MenuItem value="TRUE">Yes</MenuItem>
                <MenuItem value="FALSE">No</MenuItem>
              </FilterTextField>
              <FilterTextField
                select
                label="Addon Enabled"
                value={filterDraft.addonEnabled}
                onChange={(e) => updateFilter({ addonEnabled: e.target.value as TriStateSelection })}
              >
                <MenuItem value="ANY">Any</MenuItem>
                <MenuItem value="TRUE">Yes</MenuItem>
                <MenuItem value="FALSE">No</MenuItem>
              </FilterTextField>
              <FilterTextField
                label="Start Time From"
                type="datetime-local"
                value={filterDraft.startTimeFrom}
                onChange={(e) => updateFilter({ startTimeFrom: e.target.value })}
                InputLabelProps={{ shrink: true }}
              />
              <FilterTextField
                label="Start Time To"
                type="datetime-local"
                value={filterDraft.startTimeTo}
                onChange={(e) => updateFilter({ startTimeTo: e.target.value })}
                InputLabelProps={{ shrink: true }}
              />
              <RangeField
                label="Buy-in"
                minValue={filterDraft.minBuyIn}
                maxValue={filterDraft.maxBuyIn}
                onMinChange={(v) => updateFilter({ minBuyIn: v })}
                onMaxChange={(v) => updateFilter({ maxBuyIn: v })}
              />
              <RangeField
                label="Entry Fee"
                minValue={filterDraft.minEntryFee}
                maxValue={filterDraft.maxEntryFee}
                onMinChange={(v) => updateFilter({ minEntryFee: v })}
                onMaxChange={(v) => updateFilter({ maxEntryFee: v })}
              />
              <RangeField
                label="Guaranteed Prize Pool"
                minValue={filterDraft.minGuaranteedPrizePool}
                maxValue={filterDraft.maxGuaranteedPrizePool}
                onMinChange={(v) => updateFilter({ minGuaranteedPrizePool: v })}
                onMaxChange={(v) => updateFilter({ maxGuaranteedPrizePool: v })}
              />
              <RangeField
                label="Registered Players"
                minValue={filterDraft.minRegisteredPlayers}
                maxValue={filterDraft.maxRegisteredPlayers}
                onMinChange={(v) => updateFilter({ minRegisteredPlayers: v })}
                onMaxChange={(v) => updateFilter({ maxRegisteredPlayers: v })}
                step="1"
                inputMode="numeric"
              />
              <RangeField
                label="Max Players"
                minValue={filterDraft.minMaxPlayers}
                maxValue={filterDraft.maxMaxPlayers}
                onMinChange={(v) => updateFilter({ minMaxPlayers: v })}
                onMaxChange={(v) => updateFilter({ maxMaxPlayers: v })}
                step="1"
                inputMode="numeric"
              />
            </FilterGrid>
          </FilterPanel>
        </>
      )}

      {!selectedTournament && <Box sx={{ height: '1px', background: COLORS.gradientDivider, mb: 1.5 }} />}

      <Box ref={scrollRef} onScroll={handleScroll} sx={scrollContainerSx}>
        {selectedTournament ? (
          renderTournamentDetail()
        ) : (
          <>
            {tournamentQuery.isLoading && <LoadingState />}
            {tournamentQuery.error && <ErrorState />}
            {!tournamentQuery.isLoading && !tournamentQuery.error && tournamentItems.length === 0 && (
              <EmptyState message="No tournament history yet. Enter a tournament to see your results here!" />
            )}
            {!tournamentQuery.isLoading && !tournamentQuery.error && tournamentItems.length > 0 && (
              <>
                {renderTournamentTableHeader()}
                {sort.column === 'date' ? (
                  <Stack spacing={1.5} sx={{ mt: 0.5 }}>
                    {tournamentGroups.map((group) => (
                      <Box key={group.key}>
                        <GroupHeader label={group.label} count={group.items.length} unit="tournament" />
                        <Box>{group.items.map((item, i) => renderTournamentRow(item, i))}</Box>
                      </Box>
                    ))}
                  </Stack>
                ) : (
                  <Box sx={{ mt: 0.5 }}>
                    {tournamentItems.map((item, i) => renderTournamentRow(item, i))}
                  </Box>
                )}
              </>
            )}
            {tournamentQuery.isFetchingNextPage && <LoadingMore />}
          </>
        )}
      </Box>
    </>
  );
};

// ─── Small helper used only in this file ─────────────────────────────────────

const SummaryItem: React.FC<{ label: string; value: string }> = ({ label, value }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
    <Typography sx={{ fontSize: '12px', fontWeight: 600, color: COLORS.textMuted, textTransform: 'uppercase', letterSpacing: '0.4px' }}>{label}</Typography>
    <Typography sx={{ fontSize: '13px', color: COLORS.textSecondary, fontFamily: MONO_FONT }}>{value}</Typography>
  </Box>
);

export default TournamentHistoryTab;
